import React, { useEffect, useState } from "react";
import { Link as RouterLink, useHistory } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { authenticate, getProfile, requestProlongation } from "../../actions";
import AuthLayout from "../auth/AuthLayout";
// Notice, not Alert: the same three tones the rest of the product speaks in.
// The palette is deliberately not imported here - this file already has a
// `notice` of its own, the message another page left for the form.
import { Caution, Hint, Refusal } from "../workspaces/WorkspaceUi";
import { PasswordField } from "../auth/AuthControls";
import { SHOW_PLANS } from "../../features";

const isExpiryMessage = (msg) => /expired/i.test(msg || "");

// Messages other pages can leave for the sign-in form, by key only - the
// address bar never supplies the text.
const NOTICES = {
  joined: "Your account is ready and you've joined the workspace. Sign in to continue.",
  setup: "Your instance is ready. Sign in with the admin account you just created.",
};

export default function Login() {
  const history = useHistory();

  // Where to go after signing in: a path on this site from ?next=, else home.
  // Only plain path characters are allowed - no "%", so no encoded "\" or tab
  // can decode into another site - and the result must stay on this origin.
  const next = (() => {
    const value = new URLSearchParams(window.location.search).get("next") || "";
    if (!/^\/(?!\/)[\w\-/.?=&]*$/.test(value)) return "/home";
    try {
      return new URL(value, window.location.origin).origin === window.location.origin ? value : "/home";
    } catch (err) {
      return "/home";
    }
  })();

  const notice = NOTICES[new URLSearchParams(window.location.search).get("notice")] || "";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [resuming, setResuming] = useState(() => Boolean(localStorage.getItem("auth")));
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [extension, setExtension] = useState({ state: "idle", msg: "" });

  // An existing session skips the form.
  useEffect(() => {
    const token = localStorage.getItem("auth");
    if (!token) return;
    (async () => {
      const res = await getProfile(token);
      if (res.success) {
        history.push(next);
        history.go(0);
      } else {
        localStorage.clear();
        setResuming(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setExtension({ state: "idle", msg: "" });
    const errors = {};
    if (!email.trim()) errors.email = "Enter your work email.";
    if (!password) errors.password = "Enter your password.";
    setFieldErrors(errors);
    if (Object.keys(errors).length) return;

    setBusy(true);
    const res = await authenticate(email, password);
    if (res.success) {
      localStorage.setItem("auth", res.content.token);
      history.push(next);
      history.go(0);
      return;
    }
    setBusy(false);
    setError(res.msg || "We couldn't log you in. Try again.");
  };

  const askForExtension = async () => {
    setExtension({ state: "sending", msg: "" });
    const res = await requestProlongation(email.trim().toLowerCase());
    setExtension({
      state: res.success ? "sent" : "failed",
      msg: res.msg || (res.success ? "Request sent. We'll email you." : "The request didn't go through. Try again."),
    });
  };

  if (resuming) {
    return (
      <AuthLayout>
        <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
          <CircularProgress color="primary" />
          <Typography color="text.secondary">Signing you in…</Typography>
        </Stack>
      </AuthLayout>
    );
  }

  // An ended trial, and the offer to ask for more time, go with the rest of
  // the plan wording (frontend/src/features.js SHOW_PLANS): nothing expires
  // an account while plans decide nothing, so the server no longer sends
  // this. With the switch off the server's own message is shown plainly.
  const expired = SHOW_PLANS && isExpiryMessage(error);

  return (
    <AuthLayout>
      <Typography variant="h4" component="h1" sx={{ mb: 1 }}>
        Welcome back
      </Typography>
      <Typography variant="subtitle1" sx={{ mb: 4 }}>
        Log in to your Business Flows workspace.
      </Typography>

      {notice && !error && <Hint sx={{ mb: 3 }}>{notice}</Hint>}
      {error && !expired && <Refusal sx={{ mb: 3 }}>{error}</Refusal>}
      {expired && (
        <Caution
          sx={{ mb: 3 }}
          action={
            extension.state !== "sent" ? (
              <Button
                size="small"
                onClick={askForExtension}
                disabled={extension.state === "sending" || !email.trim()}
                sx={{ flexShrink: 0 }}
              >
                {extension.state === "sending" ? "Sending…" : "Request more time"}
              </Button>
            ) : null
          }
        >
          Your trial has ended. Ask our team for more time and we'll email you.
        </Caution>
      )}
      {extension.state === "sent" && <Hint sx={{ mb: 3 }}>{extension.msg}</Hint>}
      {extension.state === "failed" && <Refusal sx={{ mb: 3 }}>{extension.msg}</Refusal>}

      <Box component="form" onSubmit={submit} noValidate>
        <Stack spacing={2.5}>
          <TextField
            label="Work email"
            type="email"
            name="email"
            autoComplete="username"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={Boolean(fieldErrors.email)}
            helperText={fieldErrors.email}
          />
          <Box>
            <PasswordField
              label="Password"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={Boolean(fieldErrors.password)}
              helperText={fieldErrors.password}
            />
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Link component={RouterLink} to="/forget-password" variant="body2">
                Forgot password?
              </Link>
            </Box>
          </Box>
          <LoadingButton type="submit" variant="contained" size="large" loading={busy} fullWidth>
            Log in
          </LoadingButton>
        </Stack>
      </Box>

      <Divider sx={{ my: 4 }} />
      <Typography variant="body2" color="text.secondary" textAlign="center">
        New to Business Flows?{" "}
        <Link component={RouterLink} to="/signup">
          Start your free trial
        </Link>
      </Typography>
    </AuthLayout>
  );
}
